import React, { useState } from 'react';
import styled from 'styled-components';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { colors } from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { createLinkedAccount, ensureParentAccountExists } from '../utils/coaInitializer';
import { upsertLocalMaster, invalidateMaster } from '../utils/masterDataStore';

/**
 * QuickAddVendorModal — "create a vendor on the fly" dialog for purchase forms
 * (Purchase Order). Captures the name, phone, NTN and Vendor Type — the
 * grouping field this system actually has for a vendor. Payment terms, bank
 * details and WHT keep the same defaults the full master uses and can be filled
 * in later under Master → Vendors.
 *
 * Performs the SAME create chain as the vendor master: ensure the parent COA
 * account exists → write the vendor → create its linked payable account → stamp
 * the account id back on the vendor. A vendor without that account looks fine in
 * the picker but cannot post a purchase, so a failure is surfaced loudly.
 */
const QuickAddVendorModal = ({ companyId, existingVendors = [], onCreated, onClose }) => {
  const { userProfile, currentUser } = useAuth();

  const [vendorName, setVendorName] = useState('');
  const [phone, setPhone] = useState('');
  const [ntn, setNtn] = useState('');
  const [vendorType, setVendorType] = useState('Supplier');
  const [saving, setSaving] = useState(false);

  // Mirror the master's VEND-0001 scheme, but derive the next number from the
  // vendor list the caller already has in memory instead of re-reading the whole
  // vendors collection (the master does a full getDocs purely to count).
  // Max+1 rather than count+1 so a deleted vendor can never hand out a code that
  // is already in use.
  const genCode = () => {
    const maxCode = (existingVendors || []).reduce((max, v) => {
      const num = parseInt(String(v.vendorCode || '').replace(/^VEND-/i, ''), 10) || 0;
      return num > max ? num : max;
    }, 0);
    return `VEND-${String(maxCode + 1).padStart(4, '0')}`;
  };

  const handleSave = async () => {
    const name = vendorName.trim();
    if (!name) { alert('Vendor name is required'); return; }
    if (!companyId) return;
    const uid = userProfile?.uid || currentUser?.uid;
    if (!uid) { alert('Not signed in properly — please reload and try again.'); return; }

    setSaving(true);
    try {
      const parentCheck = await ensureParentAccountExists(companyId, 'vendor', uid);
      if (!parentCheck.success) {
        alert(`Cannot create vendor: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
        setSaving(false);
        return;
      }

      // Full default shape, matching the vendor master.
      const vendorData = {
        vendorCode: genCode(),
        vendorName: name,
        vendorType: vendorType || 'Supplier',
        contactPerson: '',
        email: '',
        phone: phone.trim(),
        mobile: '',
        address: '',
        city: '',
        country: 'Pakistan',
        ntn: ntn.trim(),
        gstNo: '',
        paymentTerms: 30,
        bankName: '',
        bankAccountNo: '',
        bankBranch: '',
        whtApplicable: false,
        whtRate: 4.5,
        isActive: true,
        notes: '',
        currentBalance: 0,
        createdAt: new Date().toISOString(),
        createdBy: uid,
        updatedAt: new Date().toISOString(),
      };

      const vendorsRef = collection(db, 'companies', companyId, 'vendors');
      const docRef = await addDoc(vendorsRef, vendorData);

      let linked = {};
      try {
        const accountResult = await createLinkedAccount(companyId, 'vendor', docRef.id, name, uid);
        if (accountResult.success) {
          linked = {
            linkedAccountId: accountResult.accountId,
            linkedAccountCode: accountResult.accountCode,
          };
          await updateDoc(doc(vendorsRef, docRef.id), linked);
        } else {
          alert('Vendor created, but its ledger account could not be created. Open Master → Vendors and edit it before purchasing.');
        }
      } catch (accountError) {
        console.error('Error creating linked account:', accountError);
        alert('Vendor created, but its ledger account could not be created. Open Master → Vendors and edit it before purchasing.');
      }

      // Write-through so the vendor is pickable on the form immediately.
      const row = { id: docRef.id, ...vendorData, ...linked };
      await upsertLocalMaster(companyId, 'vendors', row);
      invalidateMaster(companyId, 'vendors');
      invalidateMaster(companyId, 'accounts');

      onCreated?.(row);
    } catch (e) {
      console.error('Quick add vendor failed:', e);
      alert('Failed to add vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <h3>Quick Add Vendor</h3>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          <Field>
            <label>Vendor Name *</label>
            <input
              autoFocus
              value={vendorName}
              onChange={e => setVendorName(e.target.value)}
              placeholder="e.g. Al-Noor Traders"
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            />
          </Field>
          <Row>
            <Field>
              <label>Vendor Type</label>
              <select value={vendorType} onChange={e => setVendorType(e.target.value)}>
                <option value="Supplier">Supplier</option>
                <option value="Service Provider">Service Provider</option>
                <option value="Contractor">Contractor</option>
              </select>
            </Field>
            <Field>
              <label>Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0300-1234567" />
            </Field>
          </Row>
          <Field>
            <label>NTN</label>
            <input value={ntn} onChange={e => setNtn(e.target.value)} placeholder="Optional" />
          </Field>
          <Hint>A ledger account is created automatically. Payment terms, bank details and WHT can be set later under Master → Vendors.</Hint>
        </Body>
        <Footer>
          <CancelBtn onClick={onClose} disabled={saving}>Cancel</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving}>{saving ? 'Adding…' : 'Add Vendor'}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default QuickAddVendorModal;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 3000;
`;
const Modal = styled.div`
  background: #fff; border-radius: 10px; width: 460px; max-width: 94vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25); overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: ${colors.maroon || '#800020'}; }
`;
const CloseBtn = styled.button`
  border: none; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #888;
`;
const Body = styled.div`padding: 18px;`;
const Row = styled.div`display: flex; gap: 12px; & > * { flex: 1; }`;
const Field = styled.div`
  margin-bottom: 12px; display: flex; flex-direction: column;
  label { font-size: 12px; color: #555; margin-bottom: 4px; }
  input, select {
    padding: 8px 10px; border: 1px solid #ccc; border-radius: 6px; font-size: 14px;
    &:disabled { background: #f4f4f4; color: #888; }
  }
`;
const Hint = styled.div`font-size: 11px; color: #999; margin-top: 4px;`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 14px 18px; border-top: 1px solid #eee;
`;
const CancelBtn = styled.button`
  padding: 8px 16px; border: 1px solid #ccc; background: #fff; border-radius: 6px; cursor: pointer;
`;
const SaveBtn = styled.button`
  padding: 8px 18px; border: none; background: ${colors.maroon || '#800020'}; color: #fff;
  border-radius: 6px; cursor: pointer; font-weight: 600;
  &:disabled { opacity: 0.6; cursor: default; }
`;
